import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { fmtKES } from "@/lib/format";

export default function Operations() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [stalls, setStalls] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [stallForm, setStallForm] = useState({ label: "", booked_by: "", booked: false, utilized: false, fee_paid: false });
  const [sponsorForm, setSponsorForm] = useState({ name: "", type: "league", placement: "banner", notes: "" });

  const load = async () => {
    const [c, s, sp] = await Promise.all([api.get("/config"), api.get("/stalls"), api.get("/sponsors")]);
    setCfg(c.data); setStalls(s.data); setSponsors(sp.data);
  };
  useEffect(() => { load(); }, []);

  const canManage = ["super_admin", "league_admin"].includes(user.role);

  const createStall = async () => {
    if (!stallForm.label) return toast.error("Label required");
    try {
      await api.post("/stalls", stallForm);
      setStallForm({ label: "", booked_by: "", booked: false, utilized: false, fee_paid: false });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const updateStall = async (s) => {
    try {
      await api.put(`/stalls/${s.id}`, { label: s.label, booked_by: s.booked_by || "", booked: !!s.booked, utilized: !!s.utilized, fee_paid: !!s.fee_paid });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delStall = async (id) => { if (window.confirm("Delete stall?")) { await api.delete(`/stalls/${id}`); load(); } };
  const applyPenalties = async () => {
    const r = await api.post("/stalls/apply-penalties");
    toast.success(`Penalties applied to ${r.data.affected?.length || 0} stall(s)`);
    load();
  };

  const createSponsor = async () => {
    if (!sponsorForm.name) return toast.error("Name required");
    try { await api.post("/sponsors", sponsorForm); setSponsorForm({ name: "", type: "league", placement: "banner", notes: "" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const delSponsor = async (id) => { await api.delete(`/sponsors/${id}`); load(); };

  if (!cfg) return <div className="p-8 text-zinc-500">Loading…</div>;

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Module 3 · League Logistics</div>
        <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white">Operations</h1>
      </div>

      {/* Tournament Calendar */}
      <Card className="p-6 bg-[#11241b] border-white/10 mb-6">
        <h2 className="font-anton text-2xl uppercase text-white mb-4">Tournament Calendar</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KV label="League" value={`${cfg.league_start_date} → ${cfg.league_end_date}`} />
          <KV label="Auction" value={cfg.auction_date} />
          <KV label="Registration Deadline" value={cfg.registration_fee_deadline} />
          <KV label="Min Group Matches / Team" value={cfg.min_group_matches} />
        </div>
      </Card>

      {/* Stalls */}
      <Card className="p-6 bg-[#11241b] border-white/10 mb-6">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
          <div>
            <h2 className="font-anton text-2xl uppercase text-white">Commercial Stalls</h2>
            <div className="text-xs text-zinc-400">Size {cfg.stall_size} · Penalty {fmtKES(cfg.stall_non_utilization_penalty)}</div>
          </div>
          {canManage && (
            <Button data-testid="btn-apply-penalties" onClick={applyPenalties} variant="outline" className="border-red-500/40 text-red-400">
              <AlertTriangle className="w-4 h-4 mr-1.5" /> Apply Non-Utilization Penalties
            </Button>
          )}
        </div>
        {canManage && (
          <div className="grid md:grid-cols-6 gap-2 items-end mb-4 p-3 rounded-lg bg-[#060d09] border border-white/10">
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Label</label>
              <Input data-testid="stall-label" value={stallForm.label} onChange={(e) => setStallForm({ ...stallForm, label: e.target.value })} placeholder="Stall A1" className="bg-[#060d09] border-white/10 text-white mt-1" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Booked By</label>
              <Input data-testid="stall-booked-by" value={stallForm.booked_by} onChange={(e) => setStallForm({ ...stallForm, booked_by: e.target.value })} className="bg-[#060d09] border-white/10 text-white mt-1" />
            </div>
            <div className="flex items-center gap-2">
              <Switch data-testid="stall-booked" checked={stallForm.booked} onCheckedChange={(v) => setStallForm({ ...stallForm, booked: v })} />
              <Label className="text-zinc-300">Booked</Label>
            </div>
            <Button data-testid="btn-create-stall" onClick={createStall} className="bg-[#00A86B] text-white font-bold"><Plus className="w-4 h-4" /></Button>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500 text-left">
              <tr className="border-b border-white/10"><th className="p-2">Stall</th><th>Booked By</th><th>Status</th><th>Penalty</th>{canManage && <th></th>}</tr>
            </thead>
            <tbody>
              {stalls.map((s) => (
                <tr key={s.id} className="border-b border-white/5" data-testid={`stall-row-${s.id}`}>
                  <td className="p-2 text-white">{s.label}</td>
                  <td className="text-zinc-300">{s.booked_by || "—"}</td>
                  <td>
                    <div className="flex flex-wrap gap-2">
                      <Toggle label="Booked" value={s.booked} onChange={(v) => updateStall({ ...s, booked: v })} testid={`stall-booked-${s.id}`} />
                      <Toggle label="Utilized" value={s.utilized} onChange={(v) => updateStall({ ...s, utilized: v })} testid={`stall-utilized-${s.id}`} />
                      <Toggle label="Fee Paid" value={s.fee_paid} onChange={(v) => updateStall({ ...s, fee_paid: v })} testid={`stall-fee-${s.id}`} />
                    </div>
                  </td>
                  <td className={s.penalty_applied ? "text-red-400" : "text-zinc-500"}>{s.penalty_applied ? fmtKES(s.penalty_amount) : "—"}</td>
                  {canManage && <td><Button size="sm" variant="ghost" onClick={() => delStall(s.id)} className="text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button></td>}
                </tr>
              ))}
              {stalls.length === 0 && <tr><td colSpan={5} className="text-zinc-500 text-center p-6">No stalls yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Sponsors */}
      <Card className="p-6 bg-[#11241b] border-white/10">
        <h2 className="font-anton text-2xl uppercase text-white mb-4">Sponsors & Banners</h2>
        {canManage && (
          <div className="grid md:grid-cols-5 gap-2 items-end mb-4 p-3 rounded-lg bg-[#060d09] border border-white/10">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Sponsor</label>
              <Input data-testid="sponsor-name" value={sponsorForm.name} onChange={(e) => setSponsorForm({ ...sponsorForm, name: e.target.value })} className="bg-[#060d09] border-white/10 text-white mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Type</label>
              <select data-testid="sponsor-type" value={sponsorForm.type} onChange={(e) => setSponsorForm({ ...sponsorForm, type: e.target.value })} className="w-full bg-[#060d09] border border-white/10 rounded-md px-3 py-2 text-sm text-white mt-1">
                <option value="league">League</option>
                <option value="team">Team</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Placement</label>
              <select data-testid="sponsor-placement" value={sponsorForm.placement} onChange={(e) => setSponsorForm({ ...sponsorForm, placement: e.target.value })} className="w-full bg-[#060d09] border border-white/10 rounded-md px-3 py-2 text-sm text-white mt-1">
                <option value="left_chest">Left Chest</option>
                <option value="sleeve">Sleeve</option>
                <option value="right_chest">Right Chest</option>
                <option value="back">Back (Reserved)</option>
                <option value="jersey">Jersey Body</option>
                <option value="banner">Court Banner</option>
              </select>
            </div>
            <div className="md:col-span-1">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Notes</label>
              <Input data-testid="sponsor-notes" value={sponsorForm.notes} onChange={(e) => setSponsorForm({ ...sponsorForm, notes: e.target.value })} className="bg-[#060d09] border-white/10 text-white mt-1" />
            </div>
            <Button data-testid="btn-create-sponsor" onClick={createSponsor} className="bg-[#00A86B] text-white font-bold"><Plus className="w-4 h-4" /></Button>
          </div>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {sponsors.map((s) => (
            <div key={s.id} className="p-3 rounded-lg bg-[#060d09] border border-white/10 flex items-center justify-between" data-testid={`sponsor-row-${s.id}`}>
              <div>
                <div className="font-anton text-lg uppercase text-white">{s.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{s.type} · {s.placement}</div>
                {s.notes && <div className="text-xs text-zinc-400 mt-1">{s.notes}</div>}
              </div>
              {canManage && <Button size="sm" variant="ghost" onClick={() => delSponsor(s.id)} className="text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>}
            </div>
          ))}
          {sponsors.length === 0 && <div className="col-span-full text-zinc-500 text-center py-6">No sponsors yet.</div>}
        </div>
      </Card>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-white mt-1">{value}</div>
    </div>
  );
}

function Toggle({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-1.5 text-xs">
      <Switch data-testid={testid} checked={!!value} onCheckedChange={onChange} />
      <span className={value ? "text-emerald-400" : "text-zinc-500"}>{label}</span>
    </label>
  );
}
